import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { toast } from "sonner";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import {
  loadRecents, loadFreq, recordSelection, clearRecents, makeFilter,
} from "./commandPaletteUtils";
import {
  Search, FileText, FolderOpen, Zap, Settings, ArrowRight,
  Plus, Timer, LogOut, Palette, CheckCircle, Bell, Trophy,
  StickyNote, MessageSquare, User, CreditCard, Clock, Check, ChevronLeft,
  GraduationCap, Flame,
} from "lucide-react";
import SearchOperatorPicker from "@/components/SearchOperatorPicker";
import OperatorHintPulse from "@/components/OperatorHintPulse";

/**
 * Theme cycle / submenu source — matches the curated 5-preset picker
 * in /settings/appearance. `swatch` is the bg, `accent` is the contrast.
 */
const THEMES = [
  { id: "minimalist-bw",      label: "Dark",            swatch: "#000000", accent: "#FFFFFF" },
  { id: "minimalist-bw-dark", label: "Light",           swatch: "#FFFFFF", accent: "#000000" },
  { id: "midnight-purple",    label: "Midnight Purple", swatch: "#0F0A1A", accent: "#A855F7" },
  { id: "deep-navy",          label: "Deep Navy",       swatch: "#0B0F19", accent: "#3B82F6" },
  { id: "forest",             label: "Forest",          swatch: "#0A1410", accent: "#22C55E" },
];

// Real global shortcuts wired elsewhere in the app:
//   Q  → QuickCapture (components/QuickCapture.js:61)
//   /  → Search (this file)
//   ⌘K → Command palette (this file)
// Don't fabricate hints for shortcuts that don't exist — per Phase 2 spec.
const ACTION_ITEMS = [
  { id: "new-task",            label: "New task",            icon: Plus,         action: "new-task" },
  { id: "new-note",            label: "New note",            icon: StickyNote,                       action: "new-note" },
  { id: "new-project",         label: "New project",         icon: FolderOpen,                       action: "new-project" },
  { id: "switch-theme",        label: "Switch theme…",       icon: Palette,                          action: "switch-theme" },
  { id: "toggle-theme",        label: "Toggle theme (cycle)", icon: Palette,                         action: "toggle-theme" },
  { id: "start-focus",         label: "Start focus session", icon: Timer,                            action: "start-focus" },
  { id: "quick-capture",       label: "Quick capture",       icon: Zap,          shortcut: "Q",      action: "quick-capture" },
  { id: "open-notifications",  label: "Open notifications",  icon: Bell,                             action: "open-notifications" },
  { id: "start-tutorial",      label: "Start tutorial",      icon: GraduationCap,                    action: "start-tutorial" },
  { id: "sign-out",            label: "Sign out",            icon: LogOut,                           action: "sign-out" },
];

const NAV_ITEMS = [
  { id: "nav-search",        label: "Search…",              icon: Search,    path: "/search",            shortcut: "/" },
  { id: "nav-dashboard",     label: "Go to Dashboard",      icon: ArrowRight, path: "/" },
  { id: "nav-tasks",         label: "Go to Tasks",          icon: ArrowRight, path: "/tasks" },
  { id: "nav-notes",         label: "Go to Notes",          icon: StickyNote, path: "/notes" },
  { id: "nav-milestones",    label: "Go to Milestones",     icon: Trophy,     path: "/milestones" },
  { id: "nav-habits",        label: "Go to Habits",         icon: Flame,      path: "/habits" },
  { id: "nav-feedback",      label: "Go to Feedback",       icon: MessageSquare, path: "/feedback" },
  { id: "nav-analytics",     label: "Go to Analytics",      icon: ArrowRight, path: "/analytics" },
  { id: "nav-recap",         label: "Go to Recap",          icon: ArrowRight, path: "/recap" },
  { id: "nav-activity",      label: "Go to Activity Feed",  icon: ArrowRight, path: "/activity" },
  { id: "nav-profile",       label: "Go to Profile",        icon: User,       path: "/settings/profile" },
  { id: "nav-billing",       label: "Go to Billing",        icon: CreditCard, path: "/settings/billing" },
  { id: "nav-settings",      label: "Go to Settings",       icon: Settings,   path: "/settings" },
];

const SETTINGS_ITEMS = [
  { id: "set-appearance",    label: "Appearance settings",     icon: Settings, path: "/settings/appearance" },
  { id: "set-shortcuts",     label: "Keyboard shortcuts",      icon: Settings, path: "/settings/shortcuts" },
  { id: "set-notifications", label: "Notification preferences",icon: Settings, path: "/settings/notifications" },
  { id: "set-integrations",  label: "Integrations",            icon: Settings, path: "/settings/integrations" },
  { id: "set-api-tokens",    label: "API tokens",              icon: Settings, path: "/settings/api-tokens" },
  { id: "set-export",        label: "Export data",             icon: Settings, path: "/settings/data" },
  { id: "set-advanced",      label: "Advanced settings",       icon: Settings, path: "/settings/advanced" },
];

const groupHeadingClass = "text-[10px] font-bold uppercase tracking-wider px-4";
const groupHeadingStyle = (t) => ({ color: t.textMuted });

// Look up an item's metadata by id so recordSelection can capture a label
// without forcing every onSelect site to remember to pass one.
function lookupItem(id) {
  return ACTION_ITEMS.find((a) => a.id === id)
      || NAV_ITEMS.find((n) => n.id === id)
      || SETTINGS_ITEMS.find((s) => s.id === id);
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  // mode === "theme-switcher" renders the inline theme submenu.
  const [mode, setMode] = useState(null);
  // Bumped on every successful selection so recents/freq snapshots refresh.
  const [storeTick, setStoreTick] = useState(0);

  const { tokens: t, activeThemeId, setTheme } = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const queryDebounceRef = useRef(null);
  const openTriggerRef = useRef("keyboard");

  // Cmd+K trigger + "/" → search shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        openTriggerRef.current = "keyboard";
        // One-shot conversion analytics: did the Cmd+K hint pulse
        // actually teach the user the shortcut? Fire only when the
        // hint had been shown AND we haven't already counted them.
        try {
          if (
            localStorage.getItem("mmntm:cmdk_hint_shown") === "1" &&
            localStorage.getItem("mmntm:cmdk_hint_converted") !== "1"
          ) {
            localStorage.setItem("mmntm:cmdk_hint_converted", "1");
            trackEvent("cmdk_hint_converted", {});
          }
        } catch { /* localStorage unavailable — hint conversion is best-effort */ }
        setOpen((prev) => !prev);
      }
      if (e.key === "/" && !open) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        const isEditable = tag === "input" || tag === "textarea" || tag === "select" || document.activeElement?.isContentEditable;
        if (!isEditable) {
          e.preventDefault();
          navigate("/search");
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, navigate]);

  // Fetch live data + emit "opened" event on every open;
  // emit "closed" event with summary when the palette is dismissed.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setMode(null);
      return;
    }
    trackEvent("command_palette_opened", { trigger: openTriggerRef.current });
    Promise.all([
      api.getTasks({ archived: false }).catch(() => []),
      api.getProjects().catch(() => []),
    ]).then(([tk, p]) => {
      setTasks(tk.slice(0, 100));
      setProjects(p.slice(0, 50));
    });
  }, [open]);

  // Debounced query event — fires 300ms after last keystroke. Don't fire
  // on every char to avoid flooding PostHog with partial queries.
  useEffect(() => {
    if (!open) return;
    if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current);
    queryDebounceRef.current = setTimeout(() => {
      if (search.trim().length > 0) {
        // Result count uses the visible Command items (post-filter).
        const visible = document.querySelectorAll('[data-testid="command-palette"] [cmdk-item]').length;
        trackEvent("command_palette_query", { query: search, result_count: visible });
      }
    }, 300);
    return () => { if (queryDebounceRef.current) clearTimeout(queryDebounceRef.current); };
  }, [search, open]);

  // Snapshot recents + freq each time the palette opens (or after a selection).
  // Both are read-only inside the palette's lifetime to keep filter() pure.
  const { recents, freq } = useMemo(() => ({
    recents: loadRecents(),
    freq: loadFreq(),
  // storeTick forces a re-snapshot after recordSelection writes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [open, storeTick]);

  const filterFn = useMemo(() => makeFilter({ recents, freq }), [recents, freq]);

  // Prefix mode (kept from Phase 1)
  const prefix = search.startsWith(">") ? "actions"
    : search.startsWith("#") ? "projects"
    : search.startsWith("/") ? "settings"
    : null;
  const cleanSearch = prefix ? search.slice(1).trim() : search;

  const filteredActions = useMemo(() =>
    ACTION_ITEMS.filter((item) => !cleanSearch || item.label.toLowerCase().includes(cleanSearch.toLowerCase())),
    [cleanSearch]
  );
  const filteredProjects = useMemo(() =>
    projects.filter((proj) => !cleanSearch || proj.name.toLowerCase().includes(cleanSearch.toLowerCase())).slice(0, 6),
    [projects, cleanSearch]
  );
  const filteredSettings = useMemo(() =>
    SETTINGS_ITEMS.filter((item) => !cleanSearch || item.label.toLowerCase().includes(cleanSearch.toLowerCase())),
    [cleanSearch]
  );
  const displayTasks = useMemo(() => tasks.slice(0, 8), [tasks]);

  // Resolve recent entries back to renderable items. Skip entries whose
  // referenced task/project is no longer in the live list (deleted).
  const recentItems = useMemo(() => {
    return recents
      .map((r) => {
        if (r.type === "task")    return tasks.find((tk) => tk.id === r.refId) ? r : null;
        if (r.type === "project") return projects.find((p) => p.id === r.refId) ? r : null;
        return r; // action / nav / setting
      })
      .filter(Boolean)
      .slice(0, 5);
  }, [recents, tasks, projects]);

  // Cycle to the next theme in THEMES (wraps at end).
  const cycleTheme = useCallback(() => {
    const idx = THEMES.findIndex((c) => c.id === activeThemeId);
    const next = THEMES[(idx + 1) % THEMES.length] || THEMES[0];
    setTheme(next.id);
    toast.success(`Switched to ${next.label}`);
  }, [activeThemeId, setTheme]);

  const persistAndClose = useCallback((entry) => {
    if (entry) recordSelection(entry);
    setStoreTick((n) => n + 1);
    setOpen(false);
  }, []);

  const handleSelect = useCallback((value, position = -1) => {
    // PostHog selection event for every successful pick
    const item = lookupItem(value);
    trackEvent("command_palette_selected", {
      item_type: item ? (value.startsWith("nav-") ? "nav" : value.startsWith("set-") ? "setting" : "action")
                       : value.startsWith("task-")   ? "task"
                       : value.startsWith("project-") ? "project"
                       : "unknown",
      item_id: value,
      item_label: item?.label || value,
      query: search,
      position_in_list: position,
    });

    // Navigation
    const navItem = NAV_ITEMS.find((n) => n.id === value);
    if (navItem) {
      if (navItem.id === "nav-search" && search) {
        navigate(`/search?q=${encodeURIComponent(search)}`);
      } else {
        navigate(navItem.path);
      }
      persistAndClose({ id: value, type: "nav", label: navItem.label, path: navItem.path });
      return;
    }

    // Settings
    const settItem = SETTINGS_ITEMS.find((s) => s.id === value);
    if (settItem) {
      navigate(settItem.path);
      persistAndClose({ id: value, type: "setting", label: settItem.label, path: settItem.path });
      return;
    }

    // Actions
    const action = ACTION_ITEMS.find((a) => a.id === value);
    if (action) {
      switch (action.action) {
        case "new-task":
        case "quick-capture":
          window.dispatchEvent(new CustomEvent("mmntm:quick-capture"));
          break;
        case "new-note":
          navigate("/notes?new=1");
          break;
        case "new-project":
          if (window.location.pathname !== "/") {
            navigate("/");
            setTimeout(() => window.dispatchEvent(new CustomEvent("mmntm:create-project")), 200);
          } else {
            window.dispatchEvent(new CustomEvent("mmntm:create-project"));
          }
          break;
        case "start-focus":
          navigate("/tasks?tab=timer");
          break;
        case "open-notifications":
          window.dispatchEvent(new CustomEvent("mmntm:open-notifications"));
          break;
        case "start-tutorial":
          window.dispatchEvent(new CustomEvent("mmntm:replay-tutorial"));
          break;
        case "switch-theme":
          // Open the inline submenu — DO NOT close the palette yet.
          setMode("theme-switcher");
          setSearch("");
          recordSelection({ id: value, type: "action", label: action.label });
          setStoreTick((n) => n + 1);
          return;
        case "toggle-theme":
          cycleTheme();
          break;
        case "sign-out":
          logout();
          break;
        default: break;
      }
      persistAndClose({ id: value, type: "action", label: action.label });
      return;
    }

    // Theme submenu pick
    if (value.startsWith("theme-pick-")) {
      const themeId = value.replace("theme-pick-", "");
      const themeMeta = THEMES.find((th) => th.id === themeId);
      if (themeMeta) {
        setTheme(themeId);
        toast.success(`Switched to ${themeMeta.label}`);
        recordSelection({ id: value, type: "theme", label: themeMeta.label });
      }
      setStoreTick((n) => n + 1);
      setOpen(false);
      return;
    }

    // Task — preserve task id in URL for future task-detail UI.
    if (value.startsWith("task-")) {
      const taskId = value.replace("task-", "");
      const task = tasks.find((tk) => tk.id === taskId);
      const path = task?.project_id ? `/?project=${task.project_id}&task=${taskId}` : `/tasks?task=${taskId}`;
      navigate(path);
      persistAndClose({ id: value, type: "task", refId: taskId, label: task?.title || "Task", path });
      return;
    }

    // Project
    if (value.startsWith("project-")) {
      const projectId = value.replace("project-", "");
      const proj = projects.find((p) => p.id === projectId);
      navigate(`/?project=${projectId}`);
      persistAndClose({ id: value, type: "project", refId: projectId, label: proj?.name || "Project", path: `/?project=${projectId}` });
      return;
    }

    // Recent item replay — uses the stored path directly.
    if (value.startsWith("recent-")) {
      const realId = value.replace("recent-", "");
      const rec = recents.find((r) => r.id === realId);
      if (rec?.path) {
        navigate(rec.path);
        persistAndClose(rec); // bumps recency
        return;
      }
      // Fallback: re-issue against the underlying handler
      handleSelect(realId, position);
      return;
    }

    setOpen(false);
  }, [navigate, logout, tasks, projects, search, recents, cycleTheme, persistAndClose, setTheme]);

  // Track "closed without selection" — distinct from "closed after select"
  // because persistAndClose closes the palette with a known entry.
  const closeWithoutSelection = useCallback(() => {
    if (open) {
      const visible = document.querySelectorAll('[data-testid="command-palette"] [cmdk-item]').length;
      trackEvent("command_palette_closed", { had_query: search.trim().length > 0, result_count: visible });
    }
    setOpen(false);
  }, [open, search]);

  // Escape key — handled at window level so it works even when focus has
  // shifted (e.g., user just clicked the "Clear recents" button and lost
  // input focus). Single-source close handler keeps analytics consistent.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (mode === "theme-switcher") {
        setMode(null);
        setSearch("");
      } else {
        closeWithoutSelection();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, mode, closeWithoutSelection]);

  if (!open) return null;

  const itemBaseStyle = { color: t.textPrimary };
  const itemClass =
    "flex items-center gap-2.5 px-4 py-2 cursor-pointer text-sm transition-colors " +
    "data-[selected=true]:[background:var(--tm-bg-tertiary)]";

  const kbdStyle = {
    background: t.bgTertiary,
    border: `1px solid ${t.borderColor}`,
    color: t.textSecondary,
  };

  const isThemeMode = mode === "theme-switcher";
  const showRecents = !isThemeMode && !prefix && search.trim().length === 0 && recentItems.length > 0;

  return (
    <div className="fixed inset-0 z-[190]" data-testid="command-palette">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeWithoutSelection} />
      <div className="flex items-start justify-center pt-[15vh]">
        <Command
          className="relative w-full max-w-[640px] mx-4 rounded-xl shadow-2xl overflow-hidden mmntm-popup border"
          style={{ background: t.bgCard || t.bgSecondary, borderColor: t.borderColor, color: t.textPrimary }}
          label="Command Palette"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              if (isThemeMode) { setMode(null); setSearch(""); }
              else closeWithoutSelection();
            }
          }}
          // Use our fuzzy ranker. cmdk applies it to every Command.Item value.
          // Skip filtering when in submenu mode (theme-switcher) so all
          // 5 themes are always visible regardless of input state.
          filter={isThemeMode ? () => 1 : (value, query) => filterFn(value, query)}
          // When a prefix mode is active we want our own filter — but we
          // still want cmdk to do exact-substring keyboard nav too.
          shouldFilter={!isThemeMode}
        >
          {/* Search input */}
          <div className="relative flex items-center gap-3 px-4 py-3" style={{ borderBottom: `1px solid ${t.borderColor}` }}>
            {isThemeMode ? (
              <button
                onClick={() => { setMode(null); setSearch(""); }}
                className="flex items-center gap-1 text-xs"
                style={{ color: t.textMuted }}
                aria-label="Back"
                data-testid="command-palette-back">
                <ChevronLeft className="h-4 w-4" />
              </button>
            ) : (
              <Search className="h-4 w-4 shrink-0" style={{ color: t.textMuted }} />
            )}
            <Command.Input
              ref={inputRef}
              value={search}
              onValueChange={setSearch}
              className="flex-1 text-sm bg-transparent outline-none"
              style={{ color: t.textPrimary }}
              placeholder={
                isThemeMode ? "Pick a theme…"
                : prefix === "actions" ? "Search actions…"
                : prefix === "projects" ? "Search projects…"
                : prefix === "settings" ? "Search settings…"
                : "Search tasks, projects, actions…"
              }
              data-testid="command-palette-input"
              autoFocus
            />
            {search && !isThemeMode && (
              <button onClick={() => setSearch("")} className="text-xs" style={{ color: t.textMuted }}>Clear</button>
            )}

            {/* Quick-filter picker — only in default mode (no submenu).
                z-[1100] so the dropdown floats above the palette's
                Command.List even if list items have stacking context.
                The picker stops native keydown events at capture phase,
                so cmdk's own arrow-key result navigation is bypassed
                while it's open. */}
            {!isThemeMode && !prefix && (
              <SearchOperatorPicker
                inputRef={inputRef}
                value={search}
                setValue={(next) => setSearch(next)}
                zClass="z-[1100]"
              />
            )}

            {/* One-shot ":" hint — first focus, ever, anywhere. */}
            {!isThemeMode && !prefix && (
              <OperatorHintPulse inputRef={inputRef} placement="bottom" />
            )}
          </div>

          <Command.List className="max-h-[360px] overflow-y-auto py-2" style={{ scrollbarWidth: "thin" }}>
            <Command.Empty className="px-4 py-8 text-center text-sm" style={{ color: t.textMuted }}>
              No results found
            </Command.Empty>

            {/* THEME SUBMENU — replaces all other groups when active */}
            {isThemeMode && (
              <Command.Group heading={<span className={groupHeadingClass} style={groupHeadingStyle(t)}>Switch theme</span>}>
                {THEMES.map((th) => {
                  const isActive = th.id === activeThemeId;
                  return (
                    <Command.Item
                      key={th.id}
                      value={`theme-pick-${th.id} ${th.label}`}
                      onSelect={() => handleSelect(`theme-pick-${th.id}`)}
                      className={itemClass}
                      style={itemBaseStyle}
                      data-testid={`cmd-theme-pick-${th.id}`}>
                      <span className="shrink-0 w-5 h-5 rounded-full border"
                        style={{
                          background: th.swatch,
                          borderColor: t.borderColor,
                          boxShadow: `inset 0 0 0 6px ${th.accent}`,
                        }} aria-hidden="true" />
                      <span className="flex-1">{th.label}</span>
                      {isActive && (
                        <span className="flex items-center gap-1 text-[10px]" style={{ color: t.accent }}>
                          <Check className="h-3 w-3" /> Active
                        </span>
                      )}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            )}

            {/* RECENTS — only when input is empty and not in submenu mode */}
            {showRecents && (
              <Command.Group heading={
                <span className={groupHeadingClass} style={{ ...groupHeadingStyle(t), display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-2.5 w-2.5" /> Recent
                  </span>
                  <button
                    onClick={() => { clearRecents(); setStoreTick((n) => n + 1); }}
                    className="text-[9px] normal-case font-normal"
                    style={{ color: t.textMuted }}
                    data-testid="command-palette-clear-recents">
                    Clear
                  </button>
                </span>
              }>
                {recentItems.map((r, idx) => {
                  const Icon =
                    r.type === "task"    ? FileText
                    : r.type === "project" ? FolderOpen
                    : r.type === "nav"     ? ArrowRight
                    : r.type === "setting" ? Settings
                    : r.type === "theme"   ? Palette
                    : Zap;
                  return (
                    <Command.Item
                      key={`recent-${r.id}`}
                      value={`recent-${r.id} ${r.label || ""}`}
                      onSelect={() => handleSelect(`recent-${r.id}`, idx)}
                      className={itemClass}
                      style={itemBaseStyle}
                      data-testid={`cmd-recent-${r.id}`}>
                      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: t.textMuted }} />
                      <span className="flex-1 truncate">{r.label || r.id}</span>
                      <span className="text-[9px]" style={{ color: t.textMuted }}>recent</span>
                    </Command.Item>
                  );
                })}
              </Command.Group>
            )}

            {/* Actions — promoted above Tasks/Projects so short queries
                like "n" surface "New task"/"New note" before any task whose
                title happens to contain an "n". cmdk renders groups in
                JSX order; our scorer biases scores but doesn't reorder. */}
            {!isThemeMode && (!prefix || prefix === "actions") && (
              <Command.Group heading={<span className={groupHeadingClass} style={groupHeadingStyle(t)}>Actions</span>}>
                {filteredActions.map((item, idx) => (
                  <Command.Item
                    key={item.id}
                    value={`${item.id} ${item.label}`}
                    onSelect={() => handleSelect(item.id, idx)}
                    className={itemClass}
                    style={itemBaseStyle}
                    data-testid={`cmd-action-${item.id}`}>
                    <item.icon className="h-3.5 w-3.5 shrink-0" style={{ color: t.accent }} />
                    <span className="flex-1">{item.label}</span>
                    {item.shortcut && (
                      <kbd className="text-[10px] px-1.5 py-0.5 rounded font-mono" style={kbdStyle}>{item.shortcut}</kbd>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Navigation */}
            {!isThemeMode && (!prefix || prefix === null) && (
              <Command.Group heading={<span className={groupHeadingClass} style={groupHeadingStyle(t)}>Navigation</span>}>
                {NAV_ITEMS.map((item, idx) => (
                  <Command.Item
                    key={item.id}
                    value={`${item.id} ${item.label}`}
                    onSelect={() => handleSelect(item.id, idx)}
                    className={itemClass}
                    style={itemBaseStyle}
                    data-testid={`cmd-nav-${item.id}`}>
                    <item.icon className="h-3.5 w-3.5 shrink-0" style={{ color: t.textSecondary }} />
                    <span className="flex-1">{item.label}</span>
                    {item.shortcut && (
                      <kbd className="text-[10px] px-1.5 py-0.5 rounded font-mono" style={kbdStyle}>{item.shortcut}</kbd>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Tasks */}
            {!isThemeMode && (!prefix || prefix === null) && tasks.length > 0 && (
              <Command.Group heading={<span className={groupHeadingClass} style={groupHeadingStyle(t)}>Tasks</span>}>
                {displayTasks.map((task, idx) => (
                  <Command.Item
                    key={task.id}
                    value={`task-${task.id} ${task.title}`}
                    onSelect={() => handleSelect(`task-${task.id}`, idx)}
                    className={itemClass}
                    style={itemBaseStyle}
                    data-testid={`cmd-task-${task.id}`}>
                    {task.completed
                      ? <CheckCircle className="h-3.5 w-3.5 shrink-0" style={{ color: t.success }} />
                      : <FileText className="h-3.5 w-3.5 shrink-0" style={{ color: t.textMuted }} />}
                    <span className="flex-1 truncate">{task.title}</span>
                    {task.project_id && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded shrink-0"
                        style={{ background: t.bgTertiary, color: t.textSecondary }}>project</span>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Projects */}
            {!isThemeMode && (!prefix || prefix === "projects") && projects.length > 0 && (
              <Command.Group heading={<span className={groupHeadingClass} style={groupHeadingStyle(t)}>Projects</span>}>
                {filteredProjects.map((proj, idx) => (
                  <Command.Item
                    key={proj.id}
                    value={`project-${proj.id} ${proj.name}`}
                    onSelect={() => handleSelect(`project-${proj.id}`, idx)}
                    className={itemClass}
                    style={itemBaseStyle}
                    data-testid={`cmd-project-${proj.id}`}>
                    <FolderOpen className="h-3.5 w-3.5 shrink-0" style={{ color: t.accent }} />
                    <span className="flex-1 truncate">{proj.name}</span>
                    <span className="text-[9px]" style={{ color: t.textMuted }}>{proj.status}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Settings */}
            {!isThemeMode && (!prefix || prefix === "settings") && (
              <Command.Group heading={<span className={groupHeadingClass} style={groupHeadingStyle(t)}>Settings</span>}>
                {filteredSettings.map((item, idx) => (
                  <Command.Item
                    key={item.id}
                    value={`${item.id} ${item.label}`}
                    onSelect={() => handleSelect(item.id, idx)}
                    className={itemClass}
                    style={itemBaseStyle}
                    data-testid={`cmd-setting-${item.id}`}>
                    <item.icon className="h-3.5 w-3.5 shrink-0" style={{ color: t.textMuted }} />
                    <span className="flex-1">{item.label}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>

          {/* Footer hints */}
          <div className="flex items-center gap-4 px-4 py-2"
            style={{ borderTop: `1px solid ${t.borderColor}`, color: t.textMuted }}>
            <span className="text-[10px] flex items-center gap-1">
              <kbd className="px-1 py-0 text-[9px] rounded font-mono" style={kbdStyle}>↑↓</kbd> Navigate
            </span>
            <span className="text-[10px] flex items-center gap-1">
              <kbd className="px-1 py-0 text-[9px] rounded font-mono" style={kbdStyle}>↵</kbd> Select
            </span>
            <span className="text-[10px] flex items-center gap-1">
              <kbd className="px-1 py-0 text-[9px] rounded font-mono" style={kbdStyle}>Esc</kbd> {isThemeMode ? "Back" : "Close"}
            </span>
            <span className="text-[10px] ml-auto">
              {isThemeMode ? "Pick a theme" : (
                <>Prefix: <code style={{ color: t.textSecondary }}>&gt;</code> actions{" "}
                <code style={{ color: t.textSecondary }}>#</code> projects{" "}
                <code style={{ color: t.textSecondary }}>/</code> settings</>
              )}
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
}
